import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import rentalApi from '../services/api';
import { RentPin, RentalListing, GTA_CITIES } from '../types';
import { useTheme } from '../context/ThemeContext';
import { PinPopup } from './PinPopup';
import { ListingPopup } from './ListingPopup';
import './RentMap.css';

const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1A2035' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1A2035' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8892A4' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#B0BAC8' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#8892A4' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1E3040' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#4A6741' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#283755' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1A2744' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9CA8B8' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2C4070' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1A2744' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#B8C4D4' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#1E2D4A' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#8892A4' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0D1B2E' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4A6480' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#0D1B2E' }] },
];

interface RentMapProps {
  onPinSelect?: (coordinates: [number, number]) => void;
  onRentalSelect?: (rental: RentPin | null) => void;
  onRentalsUpdate?: (rentals: RentPin[], hasMore: boolean) => void;
  onStatsUpdate?: (count: number, total: number, avg: number) => void;
  city?: string;
  showCreate?: boolean;
  selectedRental?: RentPin | null;
  priceFilter?: { min: number; max: number };
  bedroomFilter?: string | null;
  furnishFilter?: string | null;
  propTypeFilter?: string[];
  oppsAvailOnly?: boolean;
  onListingsCountUpdate?: (count: number) => void;
  externalCoordinates?: [number, number];
  panToCoords?: [number, number];
  refreshTrigger?: number;
  onDropSeekerPin?: (coords: [number, number], type: 'room' | 'property') => void;
  userLocation?: [number, number];
  showTransit?: boolean;
  onTransitToggle?: () => void;
}

interface RentMarkerData {
  position: google.maps.LatLngLiteral;
  rent: RentPin;
}

interface LocationGroup {
  position: google.maps.LatLngLiteral;
  rentals: RentPin[];
}

interface Cluster {
  position: google.maps.LatLngLiteral;
  count: number;
  avgRent: number;
  groups: LocationGroup[];
}

// Expanded to cover all GTA + Durham Region east, Burlington south, Georgina north, Kitchener west
const GTA_BOUNDS = { north: 44.5, south: 43.1, east: -78.4, west: -80.7 };
const DEFAULT_CENTER = { lat: 43.7315, lng: -79.2945 };

const CITY_BOUNDS: Record<string, { south: number; west: number; north: number; east: number }> = {
  // Toronto & inner municipalities
  Toronto:       { south: 43.6426, west: -79.3871, north: 43.8509, east: -79.0037 },

  // Peel Region
  Mississauga:   { south: 43.4754, west: -79.6440, north: 43.6490, east: -79.5049 },
  Brampton:      { south: 43.6189, west: -79.8208, north: 43.7979, east: -79.6211 },
  Caledon:       { south: 43.7315, west: -79.9537, north: 43.9709, east: -79.6227 },
  Peel:          { south: 43.4754, west: -79.9537, north: 43.9709, east: -79.5049 },

  // York Region
  Markham:       { south: 43.8509, west: -79.2945, north: 43.9340, east: -79.1878 },
  Vaughan:       { south: 43.7315, west: -79.5437, north: 43.8509, east: -79.3627 },
  'Richmond Hill': { south: 43.8509, west: -79.4437, north: 43.9140, east: -79.3227 },
  King:          { south: 43.9340, west: -79.5437, north: 44.0340, east: -79.2227 },
  Newmarket:     { south: 44.0140, west: -79.4737, north: 44.1140, east: -79.3027 },
  Aurora:        { south: 43.9940, west: -79.4737, north: 44.0640, east: -79.3627 },
  'East Gwillimbury': { south: 44.0240, west: -79.5537, north: 44.2040, east: -79.2827 },
  Georgina:      { south: 44.1040, west: -79.6537, north: 44.4240, east: -79.2527 },
  'Whitchurch-Stouffville': { south: 43.9340, west: -79.4237, north: 44.0840, east: -79.0827 },
  York:          { south: 43.7315, west: -79.6537, north: 44.4240, east: -79.0827 },

  // Durham Region
  Ajax:          { south: 43.8209, west: -79.0637, north: 43.9240, east: -78.9027 },
  Pickering:     { south: 43.8009, west: -79.1337, north: 43.9240, east: -78.9527 },
  Whitby:        { south: 43.8409, west: -79.0037, north: 43.9640, east: -78.8527 },
  Oshawa:        { south: 43.8509, west: -78.9937, north: 43.9940, east: -78.7527 },
  Clarington:    { south: 43.7509, west: -78.9237, north: 44.1040, east: -78.5027 },
  Scugog:        { south: 44.0040, west: -79.2037, north: 44.4040, east: -78.7527 },
  Uxbridge:      { south: 44.0040, west: -79.2537, north: 44.2540, east: -79.0027 },
  Brock:         { south: 44.1040, west: -79.2537, north: 44.4540, east: -78.8527 },
  Durham:        { south: 43.7509, west: -79.1337, north: 44.4540, east: -78.4527 },

  // Halton Region
  Burlington:    { south: 43.2954, west: -79.9140, north: 43.5490, east: -79.7249 },
  Oakville:      { south: 43.3954, west: -79.8040, north: 43.5590, east: -79.5749 },
  Milton:        { south: 43.4554, west: -80.0040, north: 43.6090, east: -79.7449 },
  'Halton Hills': { south: 43.4254, west: -80.0040, north: 43.5990, east: -79.7849 },
  Halton:        { south: 43.2954, west: -80.0040, north: 43.6090, east: -79.5749 },

  // Waterloo Region (Tri-Cities)
  Kitchener:     { south: 43.3840, west: -80.6040, north: 43.5240, east: -80.3540 },
  Waterloo:      { south: 43.4540, west: -80.5640, north: 43.5840, east: -80.4440 },
  Cambridge:     { south: 43.3240, west: -80.4040, north: 43.4640, east: -80.2640 },

  // Hamilton
  Hamilton:      { south: 43.2040, west: -80.0640, north: 43.3840, east: -79.6840 },
};

function groupByLocation(items: RentMarkerData[]): LocationGroup[] {
  const map = new Map<string, LocationGroup>();
  for (const item of items) {
    const key = `${item.position.lat.toFixed(4)},${item.position.lng.toFixed(4)}`;
    if (!map.has(key)) map.set(key, { position: item.position, rentals: [] });
    map.get(key)!.rentals.push(item.rent);
  }
  return Array.from(map.values());
}

function clusterGroups(groups: LocationGroup[], zoom: number): Cluster[] {
  if (zoom >= 13) {
    return groups.map(g => ({
      position: g.position,
      count: g.rentals.length,
      avgRent: Math.round(g.rentals.reduce((s, r) => s + r.monthly_rent, 0) / g.rentals.length),
      groups: [g],
    }));
  }
  const step = zoom >= 11 ? 0.02 : zoom >= 9 ? 0.06 : 0.12;
  const grid = new Map<string, LocationGroup[]>();
  for (const group of groups) {
    const gx = Math.round(group.position.lat / step);
    const gy = Math.round(group.position.lng / step);
    const key = `${gx},${gy}`;
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key)!.push(group);
  }
  return Array.from(grid.values()).map(cell => {
    const allRentals = cell.flatMap(g => g.rentals);
    return {
      position: {
        lat: cell.reduce((s, g) => s + g.position.lat, 0) / cell.length,
        lng: cell.reduce((s, g) => s + g.position.lng, 0) / cell.length,
      },
      count: allRentals.length,
      avgRent: Math.round(allRentals.reduce((s, r) => s + r.monthly_rent, 0) / allRentals.length),
      groups: cell,
    };
  });
}

function pinSvgUrl(fill: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40"><path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 26 14 26S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="${fill}"/><circle cx="14" cy="14" r="5.5" fill="white"/></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export const RentMap: React.FC<RentMapProps> = ({
  onPinSelect,
  onRentalSelect,
  onRentalsUpdate,
  onStatsUpdate,
  city,
  showCreate,
  selectedRental,
  priceFilter = { min: 0, max: 9999 },
  bedroomFilter = null,
  furnishFilter = null,
  propTypeFilter = [],
  oppsAvailOnly = false,
  onListingsCountUpdate,
  externalCoordinates,
  panToCoords,
  refreshTrigger,
  onDropSeekerPin,
  userLocation,
  showTransit = false,
  onTransitToggle,
}) => {
  const { theme } = useTheme();

  const mapRef = useRef<google.maps.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const overlaysRef = useRef<google.maps.OverlayView[]>([]);
  const listingOverlaysRef = useRef<google.maps.OverlayView[]>([]);
  const selectedMarkerRef = useRef<google.maps.Marker | null>(null);
  const transitLayerRef = useRef<google.maps.TransitLayer | null>(null);
  const userLocationMarkerRef = useRef<google.maps.Marker | null>(null);
  const prevUserLocationKeyRef = useRef<string | null>(null);

  const [rents, setRents] = useState<RentMarkerData[]>([]);
  const [listings, setListings] = useState<RentalListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<google.maps.LatLngLiteral | null>(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [mapZoom, setMapZoom] = useState(11);
  const [popupRentals, setPopupRentals] = useState<RentPin[] | null>(null);
  const [popupListing, setPopupListing] = useState<RentalListing | null>(null);
  const [outOfBoundsMsg, setOutOfBoundsMsg] = useState(false);
  const outOfBoundsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Map initialisation (with retry cap) ──────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let attempts = 0;
    const MAX_ATTEMPTS = 10;

    const initMap = () => {
      if (!window.google?.maps) {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) return;
        setTimeout(initMap, Math.min(500 * attempts, 3000));
        return;
      }
      if (mapRef.current) return;

      try {
        const map = new window.google.maps.Map(containerRef.current!, {
          zoom: 11,
          center: DEFAULT_CENTER,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          minZoom: 9,
          maxZoom: 18,
          draggableCursor: 'crosshair',
          clickableIcons: false,
          styles: theme === 'dark' ? DARK_MAP_STYLES : [],
          restriction: {
            latLngBounds: GTA_BOUNDS,
            strictBounds: false,
          },
        });

        map.addListener('zoom_changed', () => {
          setMapZoom(map.getZoom() ?? 11);
        });

        map.addListener('click', (e: google.maps.MapMouseEvent) => {
          if (e.latLng) {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            if (lat < GTA_BOUNDS.south || lat > GTA_BOUNDS.north || lng < GTA_BOUNDS.west || lng > GTA_BOUNDS.east) {
              if (outOfBoundsTimerRef.current) clearTimeout(outOfBoundsTimerRef.current);
              setOutOfBoundsMsg(true);
              outOfBoundsTimerRef.current = setTimeout(() => setOutOfBoundsMsg(false), 3000);
              return;
            }
            new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
              let cityOk = true;
              if (status === 'OK' && results?.[0]) {
                const comps = results[0].address_components ?? [];
                const cityComp = comps.find((c: google.maps.GeocoderAddressComponent) => c.types.includes('locality'));
                const resolvedCity = cityComp?.long_name ?? '';
                if (resolvedCity && !GTA_CITIES.includes(resolvedCity)) cityOk = false;
              }
              if (!cityOk) {
                if (outOfBoundsTimerRef.current) clearTimeout(outOfBoundsTimerRef.current);
                setOutOfBoundsMsg(true);
                outOfBoundsTimerRef.current = setTimeout(() => setOutOfBoundsMsg(false), 3000);
                return;
              }
              setSelectedLocation({ lat, lng });
              onPinSelect?.([lng, lat]);
            });
          }
        });

        mapRef.current = map;
        setMapInitialized(true);
      } catch (err) {
        console.error('Map init error:', err);
      }
    };

    initMap();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Dark mode ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({ styles: theme === 'dark' ? DARK_MAP_STYLES : [] });
  }, [theme]);

  // ─── City bounds ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || !mapInitialized) return;
    if (city && CITY_BOUNDS[city]) {
      const b = CITY_BOUNDS[city];
      mapRef.current.fitBounds(
        new window.google.maps.LatLngBounds(
          { lat: b.south, lng: b.west },
          { lat: b.north, lng: b.east }
        )
      );
    } else if (!city) {
      mapRef.current.setCenter(DEFAULT_CENTER);
      mapRef.current.setZoom(11);
    }
  }, [city, mapInitialized]);

  // ─── External coordinates (from form address autocomplete) ────────────────
  useEffect(() => {
    if (!externalCoordinates || !mapInitialized || !showCreate) return;
    const [lng, lat] = externalCoordinates;
    setSelectedLocation({ lat, lng });
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(16);
  }, [externalCoordinates, mapInitialized, showCreate]);

  // ─── Pan-only (from map search bar, no marker) ─────────────────────────────
  useEffect(() => {
    if (!panToCoords || !mapInitialized) return;
    const [lng, lat] = panToCoords;
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(15);
  }, [panToCoords, mapInitialized]);

  // ─── Load rentals ──────────────────────────────────────────────────────────
  const loadRents = useCallback(async () => {
    setLoading(true);
    try {
      if (city) {
        const [rentsRes, listingsRes] = await Promise.all([
          rentalApi.getRentsByCity(city),
          rentalApi.searchListings({ city, limit: 100 }),
        ]);
        setRents(formatRentData(rentsRes.data.data ?? []));
        onRentalsUpdate?.(rentsRes.data.data ?? [], rentsRes.data.hasMore);
        setListings((listingsRes.data.data ?? []).filter(l => l.latitude && l.longitude));
      } else {
        const [rentsRes, listingsRes] = await Promise.all([
          rentalApi.getRentsNearby(-79.3832, 43.6532, 150000),
          rentalApi.searchListings({ limit: 100 }),
        ]);
        const data = rentsRes.data.data ?? [];
        setRents(formatRentData(data));
        onRentalsUpdate?.(data, false);
        setListings((listingsRes.data.data ?? []).filter(l => l.latitude && l.longitude));
      }
    } catch (err) {
      console.error('Failed to load rents:', err);
      setRents([]);
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, [city, onRentalsUpdate]);

  useEffect(() => {
    loadRents();
  }, [loadRents, refreshTrigger]);

  // ─── Filter rentals ────────────────────────────────────────────────────────
  const filteredRents = useMemo(() => rents.filter(item => {
    if (item.rent.monthly_rent < priceFilter.min || item.rent.monthly_rent > priceFilter.max) return false;
    if (bedroomFilter !== null) {
      const n = parseInt(bedroomFilter);
      if (bedroomFilter.endsWith('+') ? item.rent.bedrooms < n : item.rent.bedrooms !== n) return false;
    }
    if (furnishFilter && item.rent.furnishing !== furnishFilter) return false;
    if (propTypeFilter.length > 0 && !propTypeFilter.includes(item.rent.property_type ?? '')) return false;
    return true;
  }), [rents, priceFilter, bedroomFilter, furnishFilter, propTypeFilter]);

  const filteredListings = useMemo(() =>
    listings.filter(l => propTypeFilter.length === 0 || propTypeFilter.includes(l.property_type ?? '')),
    [listings, propTypeFilter]
  );

  useEffect(() => {
    onListingsCountUpdate?.(filteredListings.length);
  }, [filteredListings.length, onListingsCountUpdate]);

  useEffect(() => {
    const count = filteredRents.length;
    const total = filteredRents.reduce((s, i) => s + i.rent.monthly_rent, 0);
    const avg = count > 0 ? Math.round(total / count) : 0;
    onStatsUpdate?.(count, total, avg);
  }, [filteredRents, onStatsUpdate]);

  // ─── Render markers / clusters ─────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || !mapInitialized) return;

    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    overlaysRef.current.forEach(o => o.setMap(null));
    overlaysRef.current = [];

    if (oppsAvailOnly) return;

    const locationGroups = groupByLocation(
      filteredRents.filter(item => !item.rent.flagged_for_removal)
    );
    const clusters = clusterGroups(locationGroups, mapZoom);

    const font = `'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
    const fmtK = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : `$${n}`;

    clusters.forEach((cluster: Cluster) => {
      const isMapCluster = cluster.groups.length > 1;
      const group = cluster.groups[0];
      const isSelected = !isMapCluster && group.rentals.some(r => r.id === selectedRental?.id);

      const overlay = new window.google.maps.OverlayView();
      let bubbleEl: HTMLDivElement | null = null;

      overlay.onAdd = function () {
        const wrap = document.createElement('div');
        wrap.style.cssText = 'position:absolute;cursor:pointer;user-select:none;';

        if (isMapCluster) {
          // ── Cluster pill ──────────────────────────────────────────────────
          const inner = document.createElement('div');
          inner.style.cssText = [
            'background:#1A2744',
            'border-radius:20px',
            'padding:5px 13px 6px',
            'box-shadow:0 2px 10px rgba(0,0,0,0.45),0 0 0 1.5px rgba(255,255,255,0.12)',
            'display:flex', 'flex-direction:column', 'align-items:center',
            'white-space:nowrap',
            `font-family:${font}`,
            'transition:transform 0.12s,background 0.12s',
          ].join(';');
          inner.innerHTML = `
            <span style="font-size:13px;font-weight:800;color:#fff;line-height:1.25;">${cluster.count} flats</span>
            <span style="font-size:10px;font-weight:500;color:rgba(255,255,255,0.6);line-height:1.35;">AVG ${fmtK(cluster.avgRent)}</span>`;
          wrap.appendChild(inner);

          wrap.setAttribute('role', 'button');
          wrap.setAttribute('tabindex', '0');
          wrap.setAttribute('aria-label', `Cluster of ${cluster.count} rentals, average ${fmtK(cluster.avgRent)}. Press to zoom in.`);

          const activateCluster = (e: Event) => {
            e.stopPropagation();
            mapRef.current?.setZoom((mapRef.current.getZoom() ?? 11) + 2);
            mapRef.current?.panTo(cluster.position);
          };
          wrap.addEventListener('mouseenter', () => { inner.style.background = '#263A6B'; inner.style.transform = 'scale(1.05)'; });
          wrap.addEventListener('mouseleave', () => { inner.style.background = '#1A2744'; inner.style.transform = ''; });
          wrap.addEventListener('click', activateCluster);
          wrap.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activateCluster(e); } });

        } else {
          // ── Individual price pill ────────────────────────────────────────
          const r0         = group.rentals[0];
          const maxReports = Math.max(...group.rentals.map(r => r.reports));
          const bg         = isSelected ? '#1A6FE8' : '#E8622A';
          const bgHover    = isSelected ? '#1558C4' : '#CF5220';

          const label = group.rentals.length > 1
            ? `${group.rentals.length} flats · ${fmtK(cluster.avgRent)}`
            : `${r0.bedrooms >= 5 ? '5+' : r0.bedrooms}BHK ${fmtK(r0.monthly_rent)}`;

          const ariaLabel = group.rentals.length > 1
            ? `${group.rentals.length} rentals at this location, average ${fmtK(cluster.avgRent)}`
            : `${r0.bedrooms >= 5 ? '5+' : r0.bedrooms} bedroom rental, ${fmtK(r0.monthly_rent)} per month`;

          const inner = document.createElement('div');
          inner.style.cssText = [
            `background:${bg}`,
            'border-radius:14px',
            'padding:4px 10px',
            'box-shadow:0 2px 8px rgba(0,0,0,0.32)',
            'position:relative',
            'white-space:nowrap',
            `font-family:${font}`,
            'transition:transform 0.12s,background 0.12s',
          ].join(';');

          inner.innerHTML = `
            <span style="font-size:12px;font-weight:700;color:#fff;line-height:1.5;">${label}</span>
            <div class="bubble-tip" style="position:absolute;bottom:-5px;left:50%;transform:translateX(-50%);border-left:5px solid transparent;border-right:5px solid transparent;border-top:5px solid ${bg};width:0;height:0;"></div>`;

          if (maxReports > 0) {
            const badge = document.createElement('div');
            badge.style.cssText = [
              'position:absolute', 'top:-7px', 'right:-8px',
              'background:#E53935', 'color:#fff',
              'font-size:9px', 'font-weight:700',
              'border-radius:99px', 'padding:1px 5px',
              'white-space:nowrap', `font-family:${font}`,
              'box-shadow:0 1px 4px rgba(0,0,0,0.3)',
            ].join(';');
            badge.textContent = `${maxReports} report${maxReports > 1 ? 's' : ''}`;
            inner.appendChild(badge);
          }

          wrap.appendChild(inner);

          wrap.setAttribute('role', 'button');
          wrap.setAttribute('tabindex', '0');
          wrap.setAttribute('aria-label', ariaLabel);

          const tip = () => inner.querySelector('.bubble-tip') as HTMLElement | null;
          const activatePin = (e: Event) => {
            e.stopPropagation();
            onRentalSelect?.(group.rentals[0]);
            setPopupRentals(group.rentals);
          };
          wrap.addEventListener('mouseenter', () => {
            inner.style.background = bgHover;
            inner.style.transform = 'scale(1.07)';
            const t = tip(); if (t) t.style.borderTopColor = bgHover;
          });
          wrap.addEventListener('mouseleave', () => {
            inner.style.background = bg;
            inner.style.transform = '';
            const t = tip(); if (t) t.style.borderTopColor = bg;
          });
          wrap.addEventListener('click', activatePin);
          wrap.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activatePin(e); } });
        }

        this.getPanes()?.overlayMouseTarget.appendChild(wrap);
        bubbleEl = wrap;
      };

      overlay.onRemove = function () {
        bubbleEl?.parentNode?.removeChild(bubbleEl);
        bubbleEl = null;
      };

      overlay.draw = function () {
        if (!bubbleEl) return;
        const point = this.getProjection()?.fromLatLngToDivPixel(
          new window.google.maps.LatLng(cluster.position.lat, cluster.position.lng)
        );
        if (!point) return;
        if (isMapCluster) {
          bubbleEl.style.left = `${point.x}px`;
          bubbleEl.style.top  = `${point.y}px`;
          bubbleEl.style.transform = 'translate(-50%, -50%)';
        } else {
          // Align so the pointer tip sits exactly on the coordinate
          bubbleEl.style.left = `${point.x}px`;
          bubbleEl.style.top  = `${point.y}px`;
          bubbleEl.style.transform = 'translate(-50%, calc(-100% - 7px))';
        }
      };

      overlay.setMap(mapRef.current);
      overlaysRef.current.push(overlay);
    });
  }, [filteredRents, mapInitialized, mapZoom, selectedRental, onRentalSelect, oppsAvailOnly]);

  // ─── Render listing (available rooms/units) overlays ─────────────────────
  useEffect(() => {
    if (!mapRef.current || !mapInitialized) return;

    listingOverlaysRef.current.forEach(o => o.setMap(null));
    listingOverlaysRef.current = [];

    const font = `'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;
    const fmtK = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K` : `${n}`;

    filteredListings.forEach(listing => {
      const isRoom   = listing.listing_type === 'RentingRoom';
      const bg       = '#16A34A';
      const bgHover  = '#15803D';
      const typeLabel = isRoom ? 'ROOM AVBL' : 'WHOLE AVBL';
      const bhkPart  = listing.bhk ? `${listing.bhk}BHK` : '';
      const pricePart = `$${fmtK(listing.budget)}`;
      const infoLine  = [bhkPart, pricePart].filter(Boolean).join(' · ');

      const overlay = new window.google.maps.OverlayView();
      let bubbleEl: HTMLDivElement | null = null;

      overlay.onAdd = function () {
        const wrap = document.createElement('div');
        wrap.style.cssText = 'position:absolute;cursor:pointer;user-select:none;';

        const inner = document.createElement('div');
        inner.style.cssText = [
          `background:${bg}`,
          'border-radius:14px',
          'padding:4px 10px 5px',
          'box-shadow:0 2px 8px rgba(0,0,0,0.32)',
          'position:relative',
          'white-space:nowrap',
          `font-family:${font}`,
          'transition:transform 0.12s,background 0.12s',
          'display:flex',
          'flex-direction:column',
          'align-items:center',
          'gap:1px',
        ].join(';');

        const tipColor = bg;
        inner.innerHTML = `
          <span style="font-size:9px;font-weight:600;color:rgba(255,255,255,0.82);letter-spacing:0.5px;line-height:1.2;">${typeLabel}</span>
          <span style="font-size:12px;font-weight:700;color:#fff;line-height:1.3;">${infoLine}</span>
          <div class="listing-tip" style="position:absolute;bottom:-5px;left:50%;transform:translateX(-50%);border-left:5px solid transparent;border-right:5px solid transparent;border-top:5px solid ${tipColor};width:0;height:0;"></div>`;

        wrap.appendChild(inner);

        wrap.setAttribute('role', 'button');
        wrap.setAttribute('tabindex', '0');
        const listingAriaLabel = isRoom
          ? `Room available${listing.bhk ? `, ${listing.bhk} BHK` : ''}, $${fmtK(listing.budget)} per month`
          : `Whole unit available${listing.bhk ? `, ${listing.bhk} BHK` : ''}, $${fmtK(listing.budget)} per month`;
        wrap.setAttribute('aria-label', listingAriaLabel);

        const getTip = () => inner.querySelector('.listing-tip') as HTMLElement | null;
        const activateListing = (e: Event) => {
          e.stopPropagation();
          setPopupListing(listing);
        };
        wrap.addEventListener('mouseenter', () => {
          inner.style.background = bgHover;
          inner.style.transform = 'scale(1.07)';
          const t = getTip(); if (t) t.style.borderTopColor = bgHover;
        });
        wrap.addEventListener('mouseleave', () => {
          inner.style.background = bg;
          inner.style.transform = '';
          const t = getTip(); if (t) t.style.borderTopColor = bg;
        });
        wrap.addEventListener('click', activateListing);
        wrap.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activateListing(e); } });

        this.getPanes()?.overlayMouseTarget.appendChild(wrap);
        bubbleEl = wrap;
      };

      overlay.onRemove = function () {
        bubbleEl?.parentNode?.removeChild(bubbleEl);
        bubbleEl = null;
      };

      overlay.draw = function () {
        if (!bubbleEl) return;
        const point = this.getProjection()?.fromLatLngToDivPixel(
          new window.google.maps.LatLng(listing.latitude, listing.longitude)
        );
        if (!point) return;
        bubbleEl.style.left = `${point.x}px`;
        bubbleEl.style.top  = `${point.y}px`;
        bubbleEl.style.transform = 'translate(-50%, calc(-100% - 7px))';
      };

      overlay.setMap(mapRef.current);
      listingOverlaysRef.current.push(overlay);
    });
  }, [filteredListings, mapInitialized]);

  // ─── Selected-location marker (create mode) ────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || !mapInitialized || !showCreate) return;

    selectedMarkerRef.current?.setMap(null);
    selectedMarkerRef.current = null;

    if (selectedLocation) {
      selectedMarkerRef.current = new window.google.maps.Marker({
        position: selectedLocation,
        map: mapRef.current,
        title: 'Selected Location',
        icon: {
          url: pinSvgUrl('#E85D3C'),
          scaledSize: new window.google.maps.Size(28, 40),
          anchor: new window.google.maps.Point(14, 40),
        },
      });
    }
  }, [selectedLocation, mapInitialized, showCreate]);

  // ─── Transit layer ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapInitialized || !mapRef.current) return;
    if (!transitLayerRef.current) {
      transitLayerRef.current = new window.google.maps.TransitLayer();
    }
    transitLayerRef.current.setMap(showTransit ? mapRef.current : null);
  }, [showTransit, mapInitialized]);

  // ─── User location dot ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapInitialized || !mapRef.current) return;
    if (!userLocation) {
      userLocationMarkerRef.current?.setMap(null);
      userLocationMarkerRef.current = null;
      prevUserLocationKeyRef.current = null;
      return;
    }
    const [lng, lat] = userLocation;
    const key = `${lng.toFixed(5)},${lat.toFixed(5)}`;
    if (prevUserLocationKeyRef.current === key) return;
    prevUserLocationKeyRef.current = key;

    userLocationMarkerRef.current?.setMap(null);
    userLocationMarkerRef.current = null;
    const svg = encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">` +
      `<circle cx="11" cy="11" r="10" fill="rgba(26,111,232,0.18)" stroke="rgba(26,111,232,0.35)" stroke-width="1"/>` +
      `<circle cx="11" cy="11" r="6" fill="#1A6FE8" stroke="white" stroke-width="2.5"/>` +
      `</svg>`
    );
    userLocationMarkerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapRef.current,
      title: 'Your location',
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${svg}`,
        scaledSize: new window.google.maps.Size(22, 22),
        anchor: new window.google.maps.Point(11, 11),
      },
      zIndex: 999,
    });
    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(15);
  }, [userLocation, mapInitialized]);

  return (
    <div className="rent-map-container">
      <div ref={containerRef} className="map" />
      {loading && <div className="loading">Loading rents…</div>}
      {!mapInitialized && <div className="loading">Initializing map…</div>}
      {outOfBoundsMsg && (
        <div className="map-out-of-bounds">
          Sorry, we don't operate in this zone yet.
        </div>
      )}
      {popupRentals && (
        <PinPopup
          rentals={popupRentals}
          onClose={() => {
            setPopupRentals(null);
            onRentalSelect?.(null);
          }}
          onDropSeekerPin={onDropSeekerPin}
          onPinDeleted={() => { setPopupRentals(null); onRentalSelect?.(null); loadRents(); }}
          onPinUpdated={() => loadRents()}
        />
      )}
      {popupListing && (
        <ListingPopup
          listing={popupListing}
          onClose={() => setPopupListing(null)}
        />
      )}
    </div>
  );
};

function formatRentData(rentList: RentPin[]): RentMarkerData[] {
  return rentList
    .filter(r => r.latitude && r.longitude)
    .filter(r =>
      r.latitude  >= GTA_BOUNDS.south && r.latitude  <= GTA_BOUNDS.north &&
      r.longitude >= GTA_BOUNDS.west  && r.longitude <= GTA_BOUNDS.east
    )
    .map(rent => ({
      position: { lat: rent.latitude, lng: rent.longitude },
      rent,
    }));
}
